import React from 'react';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import '../../css/a3-print.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullseye,
  faCogs,
  faFileSignature,
  faPuzzlePiece,
  faQuestion,
  faRuler,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/free-brands-svg-icons';
import useFitSingleLineText from './useFitSingleLineText';
import { useParams } from 'react-router';

type ImgCell = { src: string; alt?: string };

const pageWidthPt = 17 * 72; // 1224
const pageHeightPt = 11 * 72; // 792
const marginPt = 24;
const cellPaddingPt = 6;
const cellGapPt = 4;
const border = false;
const fontFamily = 'Roboto';
const fontSizePt = 12;

const pt = (n: number) => `${Math.max(0, n)}pt`;

export default function A3PrintView() {
  const { key } = useParams<{
    key?: string;
  }>();
  const history = useHistory();
  const project = useSelector(getProjectState);

  const {
    row1Html,
    row2Images,
    row3Html,
    row4Html,
    clientName,
    projectName,
    projectType,
    facilityTypes,
  } = history.location.state as {
    row1Html: [string, string, string];
    row3Html: [string, string, string];
    row4Html: [string, string, string];
    row2Images: [ImgCell, ImgCell, ImgCell];
    clientName: string;
    projectName: string;
    projectType: string;
    facilityTypes: string;
  };

  const safeHtmlRow = (row?: [string, string, string]) =>
    (row ?? ['', '', '']) as [string, string, string];

  const safeImgRow = (row?: [ImgCell, ImgCell, ImgCell]) =>
    (row ?? [{ src: '' }, { src: '' }, { src: '' }]) as [ImgCell, ImgCell, ImgCell];

  const r1 = safeHtmlRow(row1Html);
  const r3 = safeHtmlRow(row3Html);
  const r4 = safeHtmlRow(row4Html);
  const imgs = safeImgRow(row2Images);

  const headerH = pageHeightPt * 0.05;

  const contentH = Math.max(0, pageHeightPt - headerH);

  const innerW = Math.max(0, pageWidthPt - marginPt * 2);
  const innerH = Math.max(0, contentH - marginPt * 2);

  const usableW = Math.max(0, innerW - 2 * cellGapPt);
  const usableH = Math.max(0, innerH - 3 * cellGapPt);

  const cellW = usableW / 3;
  const cellH = usableH / 4;

  const cellBorder = border ? '1pt solid #999' : 'none';

  const facilityWrapRef = React.useRef<HTMLSpanElement>(null);
  const facilityTextRef = React.useRef<HTMLSpanElement>(null);

  useFitSingleLineText({
    containerRef: facilityWrapRef,
    textRef: facilityTextRef,
    deps: [facilityTypes, clientName, projectName, projectType], // anything that affects header widths
    minPx: 10,
    maxPx: 16, // your intended “normal” size
  });

  const pageStyle: React.CSSProperties = {
    width: pt(pageWidthPt),
    height: pt(pageHeightPt),
    boxSizing: 'border-box',
    fontFamily,
    fontSize: pt(fontSizePt),
    fontWeight: 400,
    color: '#000',
    lineHeight: 1.2,
    background: '#fff',
    overflow: 'hidden',
    textTransform: 'none',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    width: '100%',
    height: pt(headerH),
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background: '#19a1da',
    paddingLeft: pt(marginPt),
    paddingRight: pt(marginPt),
  };

  const headerTextStyle: React.CSSProperties = {
    color: 'white',
    fontWeight: 600,
    fontSize: 28,
    whiteSpace: 'pre',
  };

  const contentWrapStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    height: pt(contentH),
    padding: pt(marginPt),
    overflow: 'hidden',
  };

  const tableStyle: React.CSSProperties = {
    width: pt(innerW),
    height: pt(innerH),
    borderCollapse: 'separate',
    borderSpacing: `${pt(cellGapPt)} ${pt(cellGapPt)}`,
    tableLayout: 'fixed',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 24,
    textTransform: 'uppercase',
  };

  const tdStyle: React.CSSProperties = {
    width: pt(cellW),
    height: pt(cellH),
    border: cellBorder,
    padding: pt(cellPaddingPt),
    verticalAlign: 'top',
    overflow: 'hidden',
  };

  const htmlBoxStyle: React.CSSProperties = {
    overflow: 'hidden',
    wordBreak: 'break-word',
  };

  const imgStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  };

  const HtmlTd: React.FC<{ html: string; title?: string; icon?: IconDefinition }> = ({
    html,
    title,
    icon,
  }) => (
    <td className="ql-editor" style={tdStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        {icon ? <FontAwesomeIcon icon={icon} style={{ fontSize: 32, marginRight: 12 }} /> : null}
        <p style={titleStyle}>{title}</p>
      </div>
      <div style={htmlBoxStyle} dangerouslySetInnerHTML={{ __html: html || '' }} />
    </td>
  );

  const ImgTd: React.FC<{ src: string; alt?: string }> = ({ src, alt }) => (
    <td style={{ ...tdStyle, padding: 0 }}>
      {src ? (
        <img src={src} alt={alt || ''} style={imgStyle} />
      ) : (
        <div
          style={{
            ...htmlBoxStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <span style={{ color: '#666' }}>No image</span>
        </div>
      )}
    </td>
  );

  return (
    <>
      <main className="a3-print-view">
        <div style={pageStyle}>
          <div style={headerStyle}>
            <span style={headerTextStyle}>{clientName} |</span>
            <span style={headerTextStyle}> {projectName} |</span>
            <span style={headerTextStyle}> {projectType} +</span>
            <span
              ref={facilityWrapRef}
              style={{
                flex: 1,
                minWidth: 0,
                marginLeft: 8,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'flex-end',
              }}
            >
              <span
                ref={facilityTextRef}
                style={{
                  ...headerTextStyle,
                  fontSize: 16,
                  marginTop: 8,
                  whiteSpace: 'nowrap',
                }}
              >
                {facilityTypes}
              </span>
            </span>
          </div>

          <div style={contentWrapStyle}>
            <table style={tableStyle}>
              <tbody>
                <tr>
                  <HtmlTd html={r1[0]} title="Project Metrics" />
                  <HtmlTd html={r1[1]} title="Obstacles and Insights" />
                  <HtmlTd html={r1[2]} title="Highlights" />
                </tr>
                <tr>
                  <ImgTd src={imgs[0].src} alt={imgs[0].alt} />
                  <ImgTd src={imgs[1].src} alt={imgs[1].alt} />
                  <ImgTd src={imgs[2].src} alt={imgs[2].alt} />
                </tr>
                <tr>
                  <HtmlTd html={r3[0]} title="The Why" icon={faQuestion} />
                  <HtmlTd html={r3[1]} title="Current State" icon={faRuler} />
                  <HtmlTd html={r3[2]} title="Solution Approach" icon={faPuzzlePiece} />
                </tr>
                <tr>
                  <HtmlTd html={r4[0]} title="Gap Analysis" icon={faFileSignature} />
                  <HtmlTd html={r4[1]} title="Target State" icon={faBullseye} />
                  <HtmlTd html={r4[2]} title="Impact" icon={faCogs} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <div
        className="hide-on-print"
        style={{ position: 'absolute', top: 62, left: 16, display: 'flex', gap: 8 }}
      >
        <Button
          color="primary"
          variant="contained"
          onClick={() =>
            history.push(
              key ? `/main/public/projects/${key}/a3` : `/main/projects/${project?.id}/a3`,
            )
          }
        >
          Go Back
        </Button>
        <Button color="primary" variant="contained" onClick={window.print}>
          Print
        </Button>
      </div>
    </>
  );
}
